import React from 'react';

interface BusinessReportHTMLProps {
  title: string;
  content: string;
  companyName?: string;
}

/**
 * Component for generating HTML business reports with professional formatting
 */
export const BusinessReportHTML: React.FC<BusinessReportHTMLProps> = ({ 
  title, 
  content, 
  companyName = 'Su Empresa' 
}) => {
  // Format the content into paragraphs and sections
  const formatContent = (text: string): string => {
    if (!text || text.trim() === '') {
      return '<p>No se pudo recuperar texto del documento.</p>';
    }
    
    // Separate the text into lines
    const lines = text.split(/\r?\n/);
    
    // Group lines into paragraphs
    const paragraphs: string[] = [];
    let currentParagraph = '';
    
    lines.forEach(line => {
      if (line.trim() === '') {
        if (currentParagraph !== '') {
          paragraphs.push(currentParagraph);
          currentParagraph = '';
        }
      } else {
        if (currentParagraph !== '') {
          currentParagraph += ' ';
        }
        currentParagraph += line.trim();
      }
    });
    
    if (currentParagraph !== '') {
      paragraphs.push(currentParagraph);
    }
    
    // Detect subtitles (short lines that don't end with a period)
    const formattedParagraphs = paragraphs.map(paragraph => {
      // If it's short (less than 50 characters) and doesn't end with a period, it's probably a subtitle
      if (paragraph.length < 50 && !paragraph.endsWith('.') && !paragraph.endsWith('?') && !paragraph.endsWith('!')) {
        return `<h2 class="text-lg font-semibold my-3 text-blue-800">${paragraph}</h2>`;
      }
      
      // Detect if it's a list
      if (paragraph.match(/^[\d\-\*\•][\.\)\-\s]/)) {
        return `<div class="ml-5 my-2">• ${paragraph.replace(/^[\d\-\*\•][\.\)\-\s]/, '')}</div>`;
      }
      
      // Normal paragraph
      return `<p class="my-2 text-gray-700">${paragraph}</p>`;
    });
    
    // Highlight business keywords
    const keywordHighlighter = (text: string): string => {
      // List of potential business keywords
      const keywords = [
        'objetivo', 'estrategia', 'misión', 'visión', 'valores',
        'rentabilidad', 'beneficio', 'crecimiento', 'innovación', 'desarrollo',
        'cliente', 'mercado', 'proyecto', 'producto', 'servicio',
        'análisis', 'informe', 'propuesta', 'presupuesto', 'inversión',
        'plan', 'programa', 'implementación', 'ejecución', 'evaluación',
        'rendimiento', 'eficiencia', 'productividad', 'calidad', 'mejora'
      ];
      
      let highlightedText = text;
      keywords.forEach(keyword => {
        // Highlight the keyword only if it's a complete word
        const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
        highlightedText = highlightedText.replace(regex, match => `<strong class="text-blue-600">${match}</strong>`);
      });
      
      return highlightedText;
    };
    
    // Apply keyword highlighting to all paragraphs
    const highlightedParagraphs = formattedParagraphs.map(paragraph => {
      return paragraph.replace(/>([^<]+)</g, (match, content) => {
        return '>' + keywordHighlighter(content) + '<';
      });
    });
    
    return highlightedParagraphs.join('\n');
  };

  // Generate the complete HTML document
  const generateHTML = (): string => {
    const formattedContent = formatContent(content);
    const currentDate = new Date().toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    return `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            font-family: 'Segoe UI', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
          h1 {
            color: #2c5282;
            border-bottom: 2px solid #cbd5e0;
            padding-bottom: 10px;
            font-size: 24px;
            text-align: center;
          }
          h2 {
            color: #2b6cb0;
            margin-top: 20px;
            font-size: 18px;
          }
          p {
            margin: 12px 0;
            text-align: justify;
          }
          .header {
            text-align: right;
            color: #718096;
            font-size: 14px;
            margin-bottom: 30px;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 12px;
            color: #718096;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
          }
          .company-info {
            font-style: italic;
            margin-top: 5px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${currentDate}
        </div>
        
        <h1>${title || 'Documento Empresarial'}</h1>
        
        ${formattedContent}
        
        <div class="footer">
          <p>Documento generado automáticamente</p>
          <p class="company-info">© ${new Date().getFullYear()} ${companyName}</p>
        </div>
      </body>
      </html>
    `;
  };

  const downloadHTML = () => {
    try {
      const htmlContent = generateHTML();
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.download = `informe-${timestamp}.html`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading HTML report:', error);
      alert('Error al descargar el informe HTML');
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold mb-3 text-blue-700">Informe HTML Empresarial</h3>
      <p className="text-sm text-gray-600 mb-4">
        Genera un documento HTML con formato profesional empresarial, incluyendo resaltado de palabras clave, 
        formato de secciones y estilo corporativo.
      </p>
      
      <div className="flex flex-col space-y-3">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Título:</span>
          <span className="text-sm text-gray-600">{title || 'Documento Empresarial'}</span>
        </div>
        
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Empresa:</span>
          <span className="text-sm text-gray-600">{companyName}</span>
        </div>
        
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Contenido:</span>
          <span className="text-sm text-gray-600 truncate">{content.substring(0, 50)}...</span>
        </div>
      </div>
      
      <button
        onClick={downloadHTML}
        className="mt-4 w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
      >
        Descargar Informe HTML
      </button>
    </div>
  );
};